import React from 'react';
import { Menu, X, Phone, ChevronDown, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../lib/i18n/LanguageContext';

const PHONE_NUMBER = '+4917624938417';
const PHONE_DISPLAY = '+49 176 2493 8417';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [langOpen, setLangOpen] = React.useState(false);
  const langRef = React.useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const { setLanguage } = useLanguage();

  const navLinks = [
    { href: '#advantages', labelKey: 'simonsMarketing.nav.advantages' },
    { href: '#testimonials', labelKey: 'simonsMarketing.nav.testimonials' },
    { href: '#process', labelKey: 'simonsMarketing.nav.process' },
    { href: '#guarantee', labelKey: 'simonsMarketing.nav.guarantee' },
    { href: '#faq', labelKey: 'simonsMarketing.nav.questions' },
  ];

  const languages = [
    { code: 'en', label: 'English', flag: 'EN' },
    { code: 'de', label: 'Deutsch', flag: 'DE' },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMenuOpen(false);
  };

  const handleLanguageChange = (code: string) => {
    setLanguage(code as 'en' | 'de');
    setLangOpen(false);
  };

  const linkClasses = "text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-0.5 after:bg-emerald-500 after:transition-all hover:after:w-full";
  const mobileLinkClasses = "block px-4 py-3 rounded-lg text-gray-700 font-medium hover:bg-emerald-50 hover:text-emerald-700 transition-colors";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/MainLogo.webp" alt="Simons Marketing Logo" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold text-gray-900 tracking-tight">
              Simons <span className="text-emerald-600">Marketing</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleSmoothScroll(e, link.href)}
                className={linkClasses}
              >
                {t(link.labelKey)}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <Globe className="w-4 h-4" />
                {currentLang.flag}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                        i18n.language === lang.code
                          ? 'bg-emerald-50 text-emerald-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold text-xs w-6">{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 font-semibold text-sm hover:bg-emerald-100 transition-colors"
            >
              <Phone className="w-4 h-4" />
              {PHONE_DISPLAY}
            </a>
            <Link
              to="/free-analysis"
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors shadow-sm shadow-emerald-600/20"
            >
              {t('simonsMarketing.nav.free_analysis')}
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <Link
              to="/free-analysis"
              className="flex items-center px-3 py-1.5 rounded-full bg-emerald-600 text-white font-semibold text-xs hover:bg-emerald-700 transition-colors"
            >
              {t('simonsMarketing.nav.free_analysis')}
            </Link>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-700 hover:text-emerald-600 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMenuOpen ? 'max-h-96 border-t border-gray-100' : 'max-h-0'
        }`}
      >
        <div className="px-4 py-4 bg-white space-y-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleSmoothScroll(e, link.href)}
              className={mobileLinkClasses}
            >
              {t(link.labelKey)}
            </a>
          ))}
          <Link
            to="/free-analysis"
            onClick={() => setIsMenuOpen(false)}
            className="block mx-4 mt-2 px-4 py-3 rounded-xl bg-emerald-600 text-white text-center font-semibold hover:bg-emerald-700 transition-colors"
          >
            {t('simonsMarketing.nav.free_analysis')}
          </Link>
          <div className="flex gap-2 px-4 py-3">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { handleLanguageChange(lang.code); setIsMenuOpen(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  i18n.language === lang.code
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {lang.flag} {lang.label}
              </button>
            ))}
          </div>
          <a
            href={`tel:${PHONE_NUMBER}`}
            className="flex items-center gap-2 px-4 py-3 rounded-lg text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors"
          >
            <Phone className="w-4 h-4" />
            {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
